import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AddUserToDivisionDialog from '@/components/customercenter-ui/components/modals/common/AddUserToDivision';
import { useToast } from '@/components/customercenter-ui/components/ui/use-toast';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import { ErrorNotification } from '@/helpers/utils/notification';
import {
  formatAmount,
  formatMobileNumber,
  getCookieValue,
  createUrl,
  OrderDetailsUrl,
  getDivisionCode,
} from '@/helpers/utils/siteUtils';
import DivisionsColumn from './divisions';
import {
  PAYMENT_FAILURES_QUEUE_VALUE,
  SEARCH_CUSTOMER_PAGE_PATH,
  CUSTOMER_DETAILS_PAGE_PATH,
  COOKIE_KEYS,
  SITE_KEY_MAP_MO,
  INTERNET_HOLDS_QUEUE_VALUE,
} from '../../../../../../../../constants/constant';
import CustomerResultQuickView from '../../../../../modals/customerResult/QuickView';
import { CUSTOMER_SEARCH_RECORDS_KEYS, ORDER_SEARCH_RECORDS_KEYS, ORDER_SEARCH_TYPES } from '../../../../constants';
import { CustomerSearchTransformedRecord } from '../../../../types';

const Index = ({ Texts, selectedSearchType, pagePath, allRowsData, queueName, setAllRowsDataFuncRef }: any) => {
  const { toast } = useToast();
  const mailingRecordId = useSearchParams().get('mailingRecordId') ?? '';
  const isSelectedQueuePaymentFailures = queueName === PAYMENT_FAILURES_QUEUE_VALUE;
  const isSelectedQueueInternetHolds = queueName === INTERNET_HOLDS_QUEUE_VALUE;

  if (pagePath === SEARCH_CUSTOMER_PAGE_PATH) {
    return [
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.accountNumber,
        header: Texts?.accountNumber,
        render: (data: any) => {
          const selectedDivisionCode = getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '';
          const selectedCustomerData: CustomerSearchTransformedRecord = allRowsData?.filter(
            (item: any) => item?.accountNumber === data,
          )?.[0];
          const customerRecordType = selectedCustomerData?.recordType?.toLowerCase();
          const customerDivRecordType = selectedCustomerData?.divRecordType?.toLowerCase();
          const shouldAddCustomerToDiv =
            customerRecordType === 'c' && (customerDivRecordType === 'mp' || !customerDivRecordType);
          const shouldAddCustomerMaster = customerRecordType === 'p' && customerDivRecordType === 'mp';
          const showCustomerDetailsLink = !shouldAddCustomerMaster && !shouldAddCustomerToDiv;
          const customerDetailsLink = createUrl(
            selectedCustomerData?.customerId,
            selectedCustomerData?.accountNumber,
            selectedDivisionCode,
            '',
            mailingRecordId,
          );

          return (
            <>
              {showCustomerDetailsLink ? (
                <div className="gbmask font-medium text-blueLight underline">
                  <Link href={`/${CUSTOMER_DETAILS_PAGE_PATH}/${customerDetailsLink}`}>{data}</Link>
                </div>
              ) : (
                <AddUserToDivisionDialog
                  shouldAddCustomerMaster={shouldAddCustomerMaster}
                  customerMasterData={selectedCustomerData?.masterCustomerData}
                  Texts={Texts}
                  dialogTriggerText={data}
                  dialogDescriptionText={`${Texts?.sureToAddCustomerToSite} ${
                    selectedDivisionCode &&
                    `${SITE_KEY_MAP_MO?.[selectedDivisionCode]}${selectedDivisionCode === 'ZD' ? '™' : ''}`
                  }?`}
                  divisionToAdd={selectedDivisionCode}
                  customerId={data}
                  allRowsData={allRowsData}
                  setAllRowsDataFuncRef={setAllRowsDataFuncRef}
                  accountNumber={selectedCustomerData?.accountNumber}
                />
              )}
            </>
          );
        },
      },
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.divisions,
        header: Texts?.divisions,
        render: (record?: { divisionsData: string[]; accountNumber: string }) => {
          const selectedCustomerData = allRowsData?.filter(
            (item: any) => item?.accountNumber === record?.accountNumber,
          )?.[0];
          const customerDivRecordType = selectedCustomerData?.divRecordType?.toLowerCase();
          if (customerDivRecordType === 'mp') {
            return <></>;
          }
          return (
            <DivisionsColumn
              data={record}
              Texts
              selectedCustomerData={selectedCustomerData}
              mailingRecordId={mailingRecordId}
            />
          );
        },
      },
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.lastName,
        header: Texts?.lastName,
        render: (data: any) => {
          return <p className="gbmask">{data}</p>;
        },
      },
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.firstName,
        header: Texts?.firstName,
        render: (data: any) => {
          return <p className="gbmask">{data}</p>;
        },
      },
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.addressLine1,
        header: Texts?.addressLine1,
        render: (data: any) => {
          return <p className="gbmask">{data}</p>;
        },
      },
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.zipCode,
        header: Texts?.zipCode,
        render: (data: any) => {
          return <p className="gbmask">{data}</p>;
        },
      },
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.emailAddress,
        header: Texts?.emailAddress,
        render: (data: any) => {
          return <p className="gbmask">{data}</p>;
        },
      },
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.phoneNumber,
        header: Texts?.phoneNumber,
        render: (record: string) => {
          return <p className="gbmask">{formatMobileNumber(record)}</p>;
        },
      },
      {
        key: CUSTOMER_SEARCH_RECORDS_KEYS?.accountNumber,
        header: '',
        render: (record: string) => {
          const quickViewData = allRowsData?.filter((item: any) => item?.accountNumber === record);
          const divisionData = quickViewData?.[0]?.divisions;
          return (
            <div className="font-medium text-blueLight">
              <CustomerResultQuickView
                data={quickViewData}
                Texts={Texts}
                divisionData={divisionData}
                allRowsData={allRowsData}
                setAllRowsDataFuncRef={setAllRowsDataFuncRef}
                mailingRecordId={mailingRecordId}
              />
            </div>
          );
        },
      },
    ];
  } else if (
    selectedSearchType === ORDER_SEARCH_TYPES?.orderId ||
    selectedSearchType === ORDER_SEARCH_TYPES?.recipient
  ) {
    return [
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderId,
        header: Texts?.orderId,
        render: (data: any, rowData: any) => {
          const orderId = data;
          const divisionCode = getDivisionCode(rowData?.division);
          return (
            <div className="gbmask font-medium text-blueLight underline">
              <Link
                href={`${OrderDetailsUrl(data, getDivisionCode(rowData?.division))}`}
                onClick={() => {
                  if (!orderId || !divisionCode) {
                    ErrorNotification(toast, {
                      description: `Bad request undefined data ORDER-ID:: ${orderId} DIVISION-CODE:: ${divisionCode}`,
                      action: <ErrorIcon className="h-5 w-5" />,
                    });
                  }
                }}
              >
                {data}
              </Link>
            </div>
          );
        },
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderDate,
        header: Texts?.orderDate,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.status,
        header: Texts?.status,
        render: (data: {
          statusText: string | '';
          trackingInfo: {
            trackingCode: string | '';
            trackingLink: string | '';
            externalTrackingLink?: string;
          }[];
        }) => {
          return (
            <div className="font-medium">
              <div>{data?.statusText ?? ''}</div>
              <section className="mb-1 mt-3 flex flex-col gap-3">
                {data?.trackingInfo?.length > 0 &&
                  data?.trackingInfo?.map((trackingDetail) => {
                    const trackingLink = trackingDetail?.trackingLink ?? '';
                    const trackingCode = trackingDetail?.trackingCode ?? '';
                    const externalTrackingLink = trackingDetail?.externalTrackingLink;
                    return (
                      <section key={trackingCode}>
                        <button
                          type="button"
                          className="h-[39px] w-[151px] rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-[15px] font-medium"
                          onClick={() => {
                            const trackingUrl = externalTrackingLink || trackingLink;

                            if (trackingUrl) {
                              window.open(trackingUrl, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        >
                          {Texts?.trackShipmentTitle}
                        </button>
                      </section>
                    );
                  })}
              </section>
            </div>
          );
        },
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.total,
        header: Texts?.total,
        render: (data: any) => formatAmount(data),
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.numberOfItems,
        header: Texts?.numberOfItems,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.paymentMethod,
        header: Texts?.paymentMethod,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.lastModified,
        header: Texts?.lastModified,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.origin,
        header: Texts?.origin,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.customerName,
        header: Texts?.customerName,
        render: (data: string, rowData: any) => {
          const division = getDivisionCode(rowData?.division) ?? 'ZB';
          const customerDetailsUrl = `/customer-details/${createUrl(
            rowData?.omsCustomerId,
            rowData?.omsCustomerId,
            division,
          )}`;
          return (
            <section className="gbmask">
              <Link href={customerDetailsUrl ?? ''} className="text-blueLight underline hover:no-underline">
                {data}
              </Link>
            </section>
          );
        },
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.address,
        header: Texts?.address,
        render: (data: any) => {
          return <p className="gbmask">{data}</p>;
        },
      },
    ];
  } else if (
    selectedSearchType === ORDER_SEARCH_TYPES?.orderQueues &&
    isSelectedQueueInternetHolds &&
    !isSelectedQueuePaymentFailures
  ) {
    return [
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderId,
        header: Texts?.orderId,
        render: (data: any, rowData: any) => {
          const orderId = data;
          const divisionCode = getDivisionCode(rowData?.division);
          return (
            <div className="font-medium text-blueLight underline">
              <Link
                href={`${OrderDetailsUrl(data, getDivisionCode(rowData?.division))}`}
                onClick={() => {
                  if (!orderId || !divisionCode) {
                    ErrorNotification(toast, {
                      description: `Bad request undefined data ORDER-ID:: ${orderId} DIVISION-CODE:: ${divisionCode}`,
                      action: <ErrorIcon className="h-5 w-5" />,
                    });
                  }
                }}
              >
                {data}
              </Link>
            </div>
          );
        },
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.lastName,
        header: Texts?.lastName,
        render: (data: string, rowData: any) => {
          const division = getDivisionCode(rowData?.division) ?? 'ZB';
          const customerDetailsUrl = `/customer-details/${createUrl(
            rowData?.omsCustomerId,
            rowData?.omsCustomerId,
            division,
          )}`;
          return <Link href={customerDetailsUrl ?? ''}>{data}</Link>;
        },
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.firstName,
        header: Texts?.firstName,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderDate,
        header: Texts?.orderDate,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.division,
        header: Texts?.division,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderStatus,
        header: Texts?.orderStatus,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderAmount,
        header: Texts?.orderAmount,
        render: (data: any) => formatAmount(data),
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderTerms,
        header: Texts?.orderTerms,
      },
    ];
  } else if (
    selectedSearchType === ORDER_SEARCH_TYPES?.orderQueues &&
    !isSelectedQueueInternetHolds &&
    !isSelectedQueuePaymentFailures
  ) {
    return [
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderId,
        header: Texts?.orderId,
        render: (data: any, rowData: any) => {
          const orderId = data;
          const divisionCode = getDivisionCode(rowData?.division);
          return (
            <div className="font-medium text-blueLight underline">
              <Link
                href={`${OrderDetailsUrl(data, getDivisionCode(rowData?.division))}`}
                onClick={() => {
                  if (!orderId || !divisionCode) {
                    ErrorNotification(toast, {
                      description: `Bad request undefined data ORDER-ID:: ${orderId} DIVISION-CODE:: ${divisionCode}`,
                      action: <ErrorIcon className="h-5 w-5" />,
                    });
                  }
                }}
              >
                {data}
              </Link>
            </div>
          );
        },
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.lastName,
        header: Texts?.lastName,
        render: (data: string, rowData: any) => {
          const division = getDivisionCode(rowData?.division) ?? 'ZB';
          const customerDetailsUrl = `/customer-details/${createUrl(
            rowData?.omsCustomerId,
            rowData?.omsCustomerId,
            division,
          )}`;
          return <Link href={customerDetailsUrl ?? ''}>{data}</Link>;
        },
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.firstName,
        header: Texts?.firstName,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderDate,
        header: Texts?.orderDate,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.division,
        header: Texts?.division,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderStatus,
        header: Texts?.orderStatus,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderAmount,
        header: Texts?.orderAmount,
        render: (data: any) => formatAmount(data),
      },
    ];
  } else if (selectedSearchType === ORDER_SEARCH_TYPES?.orderQueues && isSelectedQueuePaymentFailures) {
    return [
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderId,
        header: Texts?.orderId,
        render: (data: any, rowData: any) => {
          const orderId = data;
          const divisionCode = getDivisionCode(rowData?.division);
          return (
            <div className="font-medium text-blueLight underline">
              <Link
                href={`${OrderDetailsUrl(data, getDivisionCode(rowData?.division))}`}
                onClick={() => {
                  if (!orderId || !divisionCode) {
                    ErrorNotification(toast, {
                      description: `Bad request undefined data ORDER-ID:: ${orderId} DIVISION-CODE:: ${divisionCode}`,
                      action: <ErrorIcon className="h-5 w-5" />,
                    });
                  }
                }}
              >
                {data}
              </Link>
            </div>
          );
        },
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.lastName,
        header: Texts?.lastName,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.firstName,
        header: Texts?.firstName,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.channel,
        header: Texts?.channel,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderDate,
        header: Texts?.orderDate,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.division,
        header: Texts?.division,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderStatus,
        header: Texts?.orderStatus,
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.orderAmount,
        header: Texts?.orderAmount,
        render: (data: any) => formatAmount(data),
      },
      {
        key: ORDER_SEARCH_RECORDS_KEYS?.lastAttemptDate,
        header: Texts?.lastAttemptDate,
      },
    ];
  }
};

export default Index;
